import { queryClient } from "@app/QueryClientWithHeaders";
import { GraphQLSupplierRepository } from "@app/repositories/GraphQLRepositories/SupplierRepository";
import { type Pagination } from "@app/shared/types/API.types";
import { AnswerStatus } from "@generated/client/graphql";
import {
    skipToken,
    useMutation,
    useMutationState,
    useQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { supplierQuestionnairesKeys } from "../data";
import {
    filterQuestionsByDependency,
    isProofMissing,
    makeQuestionnaire,
} from "./services";

const supplierRepository = new GraphQLSupplierRepository();

export function useSupplierQuestionnaire(
    questionnaireId?: string,
    supplierId?: string,
) {
    const { data, ...query } = useQuery({
        queryKey: supplierQuestionnairesKeys.one(questionnaireId, supplierId),
        queryFn:
            questionnaireId && supplierId
                ? () =>
                      supplierRepository.getSupplierQuestionnaire(
                          questionnaireId,
                          supplierId,
                      )
                : skipToken,
    });

    const questionnaire = useMemo(() => {
        return makeQuestionnaire(data?.supplierQuestionnaire2);
    }, [data]);

    return { questionnaire, ...query };
}

export function useSupplierQuestionnaireQuestions(
    questionnaireId?: string,
    supplierId?: string,
    filter?: {
        answerStatus?: AnswerStatus[];
        proofStatus?: "missing";
        questionGroupId?: string;
    },
    pagination?: Pagination,
) {
    const { data, ...query } = useQuery({
        queryKey: supplierQuestionnairesKeys.questions(
            questionnaireId,
            supplierId,
            pagination,
        ),
        queryFn:
            questionnaireId && supplierId
                ? () =>
                      supplierRepository.getSupplierQuestionnaireQuestions(
                          questionnaireId,
                          supplierId,
                          pagination,
                      )
                : skipToken,
    });

    const questions = data?.questionnaire?.questions?.data;

    const displayedQuestions = useMemo(() => {
        return filterQuestionsByDependency(questions);
    }, [questions]);

    const missingProofQuestionsCount = useMemo(() => {
        return (
            displayedQuestions?.filter((question) => isProofMissing(question))
                .length ?? 0
        );
    }, [displayedQuestions]);

    const filteredQuestions = useMemo(() => {
        let filteredQuestions = displayedQuestions ?? [];

        if (filter?.questionGroupId) {
            filteredQuestions = filteredQuestions.filter((question) => {
                if (filter.questionGroupId === "NO_GROUP_TOKEN") {
                    return !question.questionGroupId;
                }
                return question.questionGroupId === filter.questionGroupId;
            });
        }

        if (filter?.answerStatus?.length) {
            filteredQuestions = filteredQuestions.filter((question) =>
                filter?.answerStatus?.includes(
                    question.answer?.status ?? AnswerStatus.NotAnswered,
                ),
            );
        }

        if (filter?.proofStatus) {
            filteredQuestions = filteredQuestions.filter((question) =>
                isProofMissing(question),
            );
        }

        return filteredQuestions;
    }, [
        displayedQuestions,
        filter?.answerStatus,
        filter?.proofStatus,
        filter?.questionGroupId,
    ]);

    return {
        questionList: {
            questions: filteredQuestions,
            missingProofQuestionsCount,
        },
        ...query,
    };
}

export function useUpsertSupplierQuestionnaireQuestionAnswer(
    questionId: string,
) {
    return useMutation({
        mutationKey: supplierQuestionnairesKeys.upsertAnswer(questionId),
        mutationFn:
            supplierRepository.upsertSupplierQuestionnaireQuestionAnswer,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: supplierQuestionnairesKeys.oneAll(),
            });
        },
    });
}

export const useUpdateAnswerStatus = (questionId: string | undefined) => {
    const status = useMutationState({
        filters: {
            mutationKey: supplierQuestionnairesKeys.upsertAnswer(questionId),
        },
        select: (mutation) => mutation.state.status,
    });

    return status;
};

export function useSupplierQuestionnaireGroups(
    questionnaireId?: string,
    supplierId?: string,
) {
    const { data, ...query } = useQuery({
        queryKey: supplierQuestionnairesKeys.groups(
            questionnaireId,
            supplierId,
        ),
        queryFn:
            questionnaireId && supplierId
                ? () =>
                      supplierRepository.getSupplierQuestionnaireGroups(
                          questionnaireId,
                          supplierId,
                      )
                : skipToken,
        select: (data) => data?.questionnaire?.groups,
    });

    return {
        groups: data,
        ...query,
    };
}
